<template>
  <div class="home-page">
    <NavBar />
    
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-bg" :style="{ background: `linear-gradient(270deg, rgba(140, 194, 248, 0.5) -11.7%, rgba(46, 96, 197, 0.85) 54.2%, rgba(16, 65, 181, 0.9) 100%)` }">
        <div class="container position-relative">
          <a-row justify="center" align="middle">
            <a-col :md="14" :sm="24" :xs="24">
              <div class="hero-content">
                <h1 class="hero-title">数慧岐黄</h1>
                <h2 class="hero-subtitle">“专病”—“专药”—“名医传承人”数智化平台</h2>
                <p class="hero-description">
                  连接临床与知识图谱，AI生成可溯源的个性化调理方案，支持院内制剂、远程随访与科研协同。
                </p>
                <div class="hero-search">
                  <a-input
                    v-model:value="searchKeyword"
                    size="large"
                    allow-clear
                    class="hero-search-input"
                    placeholder="搜索：专病 / 方药 / 名医（例：失眠、四君子汤、朱良春）"
                  />
                  <a-button type="primary" size="large" class="hero-search-btn" @click="handleSearch">
                    搜索
                  </a-button>
                </div>
                <div class="hero-pills">
                  <span class="hero-pill" v-for="pill in heroPills" :key="pill">{{ pill }}</span>
                </div>
                <div class="hero-actions">
                  <a-button type="primary" size="large" @click="goToChat" class="primary-btn">
                    立即体验
                  </a-button>
                  <a-button
                    v-for="action in heroActions"
                    :key="action.id"
                    size="large"
                    class="module-btn"
                    @click="openLink(action.url)"
                  >
                    {{ action.label }}
                  </a-button>
                </div>
                <div class="hero-stats">
                  <div class="hero-stat" v-for="stat in kpiStats" :key="stat.id">
                    <strong>{{ stat.displayValue }}</strong>
                    <span>{{ stat.label }}</span>
                  </div>
                </div>
              </div>
            </a-col>
            <a-col :md="10" :sm="0" :xs="0">
              <div class="hero-image">
                <img :src="require('@/assets/home/1.png')" alt="岐晓黄AI助手" class="floating-image" />
              </div>
            </a-col>
          </a-row>
        </div>
      </div>
    </section>

    <!-- Modules Section -->
    <section class="modules-section">
      <div class="container" style="max-width: 1312px;">
        <div class="section-header">
          <h2 class="section-title">核心模块</h2>
          <p class="section-description">连接临床与知识图谱，打造中医数字化能力矩阵</p>
        </div>

        <a-row :gutter="[32, 32]" justify="center">
          <a-col :md="8" :sm="12" :xs="24" v-for="module in modules" :key="module.id">
            <div class="module-card">
              <div class="module-thumb">{{ module.icon }}</div>
              <h3 class="module-title">{{ module.title }}</h3>
              <p class="module-description">{{ module.description }}</p>
              <div class="module-tags">
                <span class="module-tag" v-for="tag in module.tags" :key="tag">{{ tag }}</span>
              </div>
              <a-button class="module-btn" type="primary" size="small" @click="openLink(module.url)" >
                进入
              </a-button>
            </div>
          </a-col>
        </a-row>
      </div>
    </section>

    <!-- Season Section -->
    <section class="season-section">
      <div class="container" style="max-width: 1312px;">
        <div class="section-header">
          <h2 class="section-title">季节/节气调养</h2>
          <p class="section-description">自动感知节气，给出个性化调养方案</p>
        </div>

        <a-row :gutter="[24, 24]">
          <a-col :md="14" :sm="24" :xs="24">
            <div class="season-card">
              <h3 class="season-card-title">{{ seasonInfo.title }}</h3>
              <p class="season-card-description">{{ seasonInfo.description }}</p>
              <div class="season-tags">
                <span class="season-tag" v-for="tag in seasonTags" :key="tag">{{ tag }}</span>
              </div>
            </div>
          </a-col>
          <a-col :md="10" :sm="24" :xs="24">
            <div class="season-card highlight">
              <h3 class="season-card-title">推荐直达</h3>
              <div class="season-links">
                <span class="season-link" v-for="link in seasonQuickLinks" :key="link.label" @click="openLink(link.url)">
                  {{ link.label }}
                </span>
              </div>
              <p class="season-note">{{ seasonNote }}</p>
            </div>
          </a-col>
        </a-row>
      </div>
    </section>

    <!-- Updates Section -->
    <section class="updates-section">
      <div class="container" style="max-width: 1312px;">
        <div class="section-header">
          <h2 class="section-title">平台动态</h2>
          <p class="section-description">关注近期更新，全景掌握平台能力升级</p>
        </div>

        <div class="updates-ticker">
          <span class="ticker-item" v-for="news in newsList" :key="news">{{ news }}</span>
        </div>
      </div>
    </section>

    <!-- Partners Section -->
    <section class="partners-section">
      <div class="container" style="max-width: 1312px;">
        <div class="section-header">
          <h2 class="section-title">合作与接口</h2>
          <p class="section-description">开放能力与生态伙伴一起构建智慧中医</p>
        </div>

        <a-row :gutter="[24, 24]">
          <a-col :md="14" :sm="24" :xs="24">
            <div class="partner-card">
              <h3 class="partner-title">开放API（示例）</h3>
              <pre class="api-example">{{ apiExample }}</pre>
              <div class="module-tags">
                <span class="module-tag" v-for="tag in apiTags" :key="tag">{{ tag }}</span>
              </div>
            </div>
          </a-col>
          <a-col :md="10" :sm="24" :xs="24">
            <div class="partner-card">
              <h3 class="partner-title">商务合作</h3>
              <p class="partner-description">{{ partnerDescription }}</p>
              <div class="partner-pills">
                <span class="partner-pill" v-for="item in partnerContacts" :key="item">{{ item }}</span>
              </div>
            </div>
          </a-col>
        </a-row>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">开启您的智慧健康之旅</h2>
          <!-- <p class="cta-description">立即注册，享受专业的中医AI健康服务</p> -->
          <a-button type="primary" size="large" @click="goToChat" class="cta-btn">
            立即体验
          </a-button>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <a-row :gutter="[32, 32]">
          <a-col :md="8" :sm="24" :xs="24">
            <div class="footer-brand">
              <img src="@/assets/images/logo_full.png" alt="岐晓黄" class="footer-logo" />
              <p class="footer-description">
                数慧岐黄 - 传承中医智慧，引领健康未来
              </p>
            </div>
          </a-col>
          <a-col :md="8" :sm="12" :xs="12">
            <div class="footer-links">
              <h4 class="footer-title">快速链接</h4>
              <ul>
                <li><a href="https://www.qihuang.top/qixiaohuang.html" target="_blank">产品介绍</a></li>
                <li><a href="/chat" target="_blank">岐黄问答</a></li>
                <li><a href="/ai-doctor" target="_blank">数智郎中</a></li>
                <li><a href="https://www.qihuang.top/news.html" target="_blank">新闻中心</a></li>
                <li><a href="https://www.qihuang.top/about.html" target="_blank">关于我们</a></li>
              </ul>
            </div>
          </a-col>
          <a-col :md="8" :sm="12" :xs="12">
            <div class="footer-contact">
              <h4 class="footer-title">联系我们</h4>
              <ul>
                <li><a href="https://qihuang.top/user-agreement.html" target="_blank">用户协议</a></li>
                <li><a href="https://qihuang.top/privacy-policy.html" target="_blank">隐私政策</a></li>
              </ul>
            </div>
          </a-col>
        </a-row>
        <div class="footer-bottom">
          <p class="copyright">© 2025 岐晓黄. All rights reserved.</p>
        </div>
      </div>
    </footer>

    <!-- Floating Chat Widget -->
    <div class="chat-widget" :class="{ 'is-minimized': chatMinimized }">
      <div class="chat-header">
        <div class="chat-header-title">
          <span class="chat-header-name">数智郎中 · AI助手</span>
          <span class="chat-header-desc">体质/证型快速建议</span>
        </div>
        <button class="chat-toggle" type="button" @click="toggleChat">
          {{ chatMinimized ? '展开' : '最小化' }}
        </button>
      </div>
      <transition name="chat-fade">
        <div v-show="!chatMinimized" class="chat-body" ref="chatBody">
          <div
            v-for="message in chatMessages"
            :key="message.id"
            :class="['chat-msg', `chat-msg-${message.role}`]"
          >
            {{ message.text }}
          </div>
        </div>
      </transition>
      <transition name="chat-fade">
        <div v-show="!chatMinimized" class="chat-input">
          <a-input
            v-model:value="chatInput"
            size="small"
            class="chat-input-field"
            placeholder="例：我最近总是睡不着，怎么办？"
            @pressEnter="sendChatMessage"
          />
          <a-button type="primary" size="small" class="chat-send-btn" @click="sendChatMessage">
            发送
          </a-button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";

export default {
  name: "HomePage",
  components: {
    NavBar,
  },
  data() {
    return {
      searchKeyword: "",
      heroPills: ["🧠 AI体质识别", "👅 舌象分析", "🧪 溯源质检"],
      heroActions: [
        { id: "disease", label: "进入专病中心", url: "/zhuanbing" },
        { id: "formula", label: "进入专药智能库", url: "/zhuanyao" },
        { id: "mentor", label: "进入名医传承人", url: "/mingyi" }
      ],
      kpiStats: [
        { id: "formulas", label: "收录方药", value: 128, displayValue: 0 },
        { id: "templates", label: "证型模板", value: 42, displayValue: 0 },
        { id: "teams", label: "传承团队", value: 18, displayValue: 0 }
      ],
      modules: [
        {
          id: 1,
          icon: "🩺",
          title: "专病诊疗中心",
          description: "AI初筛、证型辨析、诊疗路径、病例随访一体化工作台。",
          tags: ["四诊合参", "中西协同"],
          url: "/zhuanbing"
        },
        {
          id: 2,
          icon: "💊",
          title: "专药智能库",
          description: "药材—方剂—中成药知识图谱，AI匹配与溯源调配一键完成。",
          tags: ["药典卡", "AI加减"],
          url: "/zhuanyao"
        },
        {
          id: 3,
          icon: "👨‍⚕️",
          title: "名医传承人",
          description: "学术思想、经典验方、授课视频、门诊排班全景呈现，助力传承创新。",
          tags: ["学派知识图谱", "AI共创助手"],
          url: "/mingyi"
        }
      ],
      seasonInfo: {
        title: "",
        description: ""
      },
      seasonTags: ["作息", "饮食", "茶饮", "运动"],
      seasonQuickLinks: [],
      seasonNote: "提示：体质差异较大，请以医师建议为准。",
      seasonConfigs: {
        default: {
          title: "立冬 · 冬令进补",
          description: "宜养肾护阳，早睡晚起，适量温补，慎过度辛辣。推荐：当归生姜羊肉汤（体质合适时）。",
          quickLinks: [
            { label: "滋阴补肾", url: "/zhuanyao" },
            { label: "冬季常见专病", url: "/zhuanbing" },
            { label: "名医冬令方案", url: "/mingyi" }
          ]
        },
        6: {
          title: "夏至 · 祛暑养心",
          description: "宜清淡易消化，合理补水，午休而不贪睡，可选淡竹叶、荷叶茶（体质适配）。",
          quickLinks: [
            { label: "夏季调养方案", url: "/zhuanbing" },
            { label: "清暑方剂推荐", url: "/zhuanyao" },
            { label: "名医祛暑思路", url: "/mingyi" }
          ]
        }
      },
      newsList: [
        "【更新】新增“证型模板”与“院内制剂联动”能力",
        "【合作】与三家中医馆达成溯源质检对接",
        "【课程】名医学派公开课上线：肝脾同治",
        "【AI】舌象分析升级，支持多张融合判断"
      ],
      apiExample: `GET  /api/herb/search?keyword=黄芪\nGET  /api/formula/info?id=001\nPOST /api/ai/recommend { physique, disease }\nGET  /api/trace/report?batch=2025-1101`,
      apiTags: ["OAuth", "Rate Limit", "Webhook"],
      partnerDescription: "支持中医馆、院校、科研机构与产业合作。可定制院内制剂与数字人教学。",
      partnerContacts: ["📧 partner@qihuang.ai", "📞 400-xxx-xxxx"],
      statTimers: [],
      chatMinimized: false,
      chatInput: "",
      chatMessages: [
        {
          id: 1,
          role: "ai",
          text: "你好，我可以基于体质/证型为你推荐路径：先到“专病”，再到“专药”。"
        }
      ],
      chatMessageId: 2
    };
  },
  mounted() {
    this.animateStats();
    this.updateSeasonInfo();
    this.$nextTick(() => {
      this.scrollChatToBottom();
    });
  },
  beforeUnmount() {
    this.clearStatTimers();
  },
  methods: {
    handleSearch() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        window.alert('请输入关键词');
        return;
      }
      window.alert(`搜索演示：${keyword}`);
    },
    openLink(url) {
      if (!url) {
        return;
      }
      if (url.startsWith('http')) {
        window.open(url, '_blank');
      } else {
        this.$router.push(url);
      }
    },
    goToChat() {
      this.$router.push('/chat');
    },
    goToQihuang() {
      this.openLink('https://www.qihuang.top/qixiaohuang.html');
    },
    animateStats() {
      this.clearStatTimers();
      this.kpiStats.forEach((stat) => {
        stat.displayValue = 0;
        const step = Math.max(1, Math.round(stat.value / 60));
        const timer = setInterval(() => {
          stat.displayValue = Math.min(stat.displayValue + step, stat.value);
          if (stat.displayValue >= stat.value) {
            clearInterval(timer);
          }
        }, 20);
        this.statTimers.push(timer);
      });
    },
    clearStatTimers() {
      this.statTimers.forEach((timer) => clearInterval(timer));
      this.statTimers = [];
    },
    updateSeasonInfo() {
      const month = new Date().getMonth() + 1;
      const config = this.seasonConfigs[month] || this.seasonConfigs.default;
      this.seasonInfo = {
        title: config.title,
        description: config.description
      };
      this.seasonQuickLinks = config.quickLinks || [];
    },
    toggleChat() {
      this.chatMinimized = !this.chatMinimized;
      if (!this.chatMinimized) {
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      }
    },
    sendChatMessage() {
      const text = this.chatInput.trim();
      if (!text) {
        return;
      }
      this.chatMessages.push({
        id: this.chatMessageId++,
        role: "user",
        text
      });
      this.chatInput = "";
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
      this.$router.push({
        name: "chat",
        query: {
          question: text
        }
      });
    },
    scrollChatToBottom() {
      const body = this.$refs.chatBody;
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.home-page {
  background: #fff;
  overflow-x: hidden;
}

.position-relative {
  position: relative;
}

// Hero Section
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  
  .hero-bg {
    width: 100%;
    min-height: 100vh;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    padding: 100px 0 60px;
  }
  
  .hero-content {
    text-align: center;
    padding: 20px;
    
    @media (min-width: 768px) {
      text-align: left;
    }
  }
  
  .hero-logo {
    width: 80px;
    margin-bottom: 20px;
    
    @media (min-width: 768px) {
      width: 100px;
    }
  }
  
  .hero-title {
    font-size: 48px;
    font-weight: 700;
    color: #fff;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    margin-bottom: 16px;
    
    @media (min-width: 768px) {
      font-size: 64px;
    }
  }
  
  .hero-subtitle {
    font-size: 18px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.95);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    margin-bottom: 24px;
    
    @media (min-width: 768px) {
      font-size: 22px;
    }
  }
  
  .hero-description {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 40px;
    line-height: 1.8;
  }

  .hero-search {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  .hero-search-input {
    flex: 1;
    min-width: 240px;
  }

  .hero-search-btn {
    flex-shrink: 0;
  }

  .hero-pills {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  .hero-pill {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    backdrop-filter: blur(6px);
  }
  
  .hero-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 32px;
    
    @media (min-width: 768px) {
      justify-content: flex-start;
    }
  }

  .hero-stats {
    display: flex;
    gap: 32px;
    flex-wrap: wrap;
  }

  .hero-stat {
    display: flex;
    flex-direction: column;
  }

  .hero-stat strong {
    font-size: 32px;
    font-weight: 700;
    color: #fff;
    line-height: 1.2;
  }

  .hero-stat span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
  }
  
  .hero-image {
    text-align: center;
    
    .floating-image {
      max-width: 100%;
      height: auto;
      animation: float 3s ease-in-out infinite;
    }
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

// Features Section
.features-section {
  padding: 80px 0;
  background: #fff;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 36px;
  font-weight: 700;
  color: #1041b5;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
}

.section-description {
  font-size: 16px;
  color: #646464;
  line-height: 1.8;
}

.feature-card {
  padding: 40px 24px;
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid rgba(94, 123, 243, 0.1);
  box-shadow: 0 2px 8px rgba(12, 12, 13, 0.08);
  transition: all 0.3s ease;
  text-align: center;
  height: 100%;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 24px rgba(94, 123, 243, 0.2);
    border-color: #5e7bf3;
  }
  
  .feature-icon {
    width: 120px;
    height: 120px;
    margin: 0 auto 24px;
    
    .feature-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      transition: transform 0.3s ease;
    }
  }
  
  .feature-title {
    font-size: 20px;
    font-weight: 600;
    color: #1041b5;
    margin-bottom: 12px;
  }
  
  .feature-description {
    font-size: 14px;
    color: #646464;
    line-height: 1.6;
    margin-bottom: 0;
  }
}

// AI Doctor Section (Scenes)
.ai-doctor-section {
  padding: 80px 0;
  background: rgba(94, 123, 243, 0.03);
}

// Modules Section
.modules-section {
  padding: 80px 0;
  background: #fff;
}

.module-card {
  height: 100%;
  padding: 32px 28px;
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid rgba(94, 123, 243, 0.1);
  box-shadow: 0 2px 8px rgba(12, 12, 13, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 24px rgba(94, 123, 243, 0.25);
    border-color: #5e7bf3;
  }
}

.module-thumb {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.1), rgba(16, 65, 181, 0.18));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #1041b5;
}

.module-title {
  font-size: 20px;
  font-weight: 600;
  color: #1041b5;
  margin: 0;
}

.module-description {
  flex: 1;
  font-size: 14px;
  color: #646464;
  line-height: 1.7;
  margin: 0;
}

.module-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.module-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(94, 123, 243, 0.08);
  color: #1041b5;
  font-size: 12px;
  font-weight: 500;
}

.module-btn {
  color: #fff;
  height: unset;
  border: none;
  background: linear-gradient(135deg, #5E7BF3 0%, #1041B5 100%);
}

// Season Section
.season-section {
  padding: 80px 0;
  background: rgba(94, 123, 243, 0.03);
}

.season-card {
  height: 100%;
  padding: 32px;
  border-radius: 16px;
  background: #fff;
  border: 1.5px solid rgba(94, 123, 243, 0.1);
  box-shadow: 0 2px 8px rgba(12, 12, 13, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.season-card.highlight {
  background: linear-gradient(135deg, rgba(140, 194, 248, 0.12), rgba(16, 65, 181, 0.12));
}

.season-card-title {
  font-size: 20px;
  font-weight: 600;
  color: #1041b5;
  margin: 0;
}

.season-card-description {
  font-size: 14px;
  color: #646464;
  line-height: 1.7;
  margin: 0;
}

.season-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.season-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(94, 123, 243, 0.08);
  color: #1041b5;
  font-size: 12px;
  font-weight: 500;
}

.season-links {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.season-link {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px dashed #5e7bf3;
  color: #1041b5;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #5e7bf3;
    color: #fff;
    border-color: #5e7bf3;
  }
}

.season-note {
  font-size: 12px;
  color: #646464;
  line-height: 1.6;
  margin: 8px 0 0;
}

// Updates Section
.updates-section {
  padding: 80px 0;
  background: #fff;
}

.updates-ticker {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(94, 123, 243, 0.1);
  background: rgba(94, 123, 243, 0.05);
}

.ticker-item {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: #fff;
  border: 1px dashed rgba(94, 123, 243, 0.4);
  color: #1041b5;
  font-size: 12px;
  white-space: nowrap;
}

// Partners Section
.partners-section {
  padding: 80px 0;
  background: rgba(94, 123, 243, 0.03);
}

.partner-card {
  height: 100%;
  padding: 32px;
  border-radius: 16px;
  background: #fff;
  border: 1.5px solid rgba(94, 123, 243, 0.1);
  box-shadow: 0 2px 8px rgba(12, 12, 13, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.partner-title {
  font-size: 20px;
  font-weight: 600;
  color: #1041b5;
  margin: 0;
}

.partner-description {
  font-size: 14px;
  color: #646464;
  line-height: 1.7;
  margin: 0;
}

.partner-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.partner-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: #fff;
  border: 1px dashed rgba(94, 123, 243, 0.4);
  color: #1041b5;
  font-size: 12px;
}

.api-example {
  margin: 0;
  padding: 16px;
  border-radius: 12px;
  background: rgba(94, 123, 243, 0.05);
  border: 1px solid rgba(94, 123, 243, 0.1);
  font-size: 12px;
  color: #1041b5;
  line-height: 1.8;
  white-space: pre-wrap;
}

// CTA Section
.cta-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #8cc2f8 0%, #5e7bf3 50%, #1041b5 100%);
  
  .cta-content {
    text-align: center;
  }
  
  .cta-title {
    font-size: 36px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 16px;
    
    @media (max-width: 768px) {
      font-size: 28px;
    }
  }
  
  .cta-description {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 32px;
  }
  
  .cta-btn {
    height: auto;
    font-size: 16px;
    font-weight: 600;
    background: #fff;
    color: #1041b5;
    border: none;
    
    &:hover {
      background: rgba(255, 255, 255, 0.9);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  }
}

// Footer
.footer {
  padding: 60px 0 20px;
  background: rgba(94, 123, 243, 0.05);
  
  .footer-brand {
    .footer-logo {
      width: 120px;
      margin-bottom: 16px;
    }
    
    .footer-description {
      font-size: 14px;
      color: #646464;
      line-height: 1.6;
    }
  }
  
  .footer-links,
  .footer-contact {
    .footer-title {
      font-size: 16px;
      font-weight: 600;
      color: #1041b5;
      margin-bottom: 16px;
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        margin-bottom: 12px;
        
        a {
          font-size: 14px;
          color: #646464;
          text-decoration: none;
          transition: color 0.3s ease;
          
          &:hover {
            color: #5e7bf3;
          }
        }
      }
    }
  }
  
  .footer-bottom {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid rgba(94, 123, 243, 0.1);
    text-align: center;
    
    .copyright {
      font-size: 14px;
      color: #999;
      margin: 0;
    }
  }
}

// Buttons
.primary-btn {
  padding: 14px 36px;
  height: auto;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: #fff;
  border: none;
  color: #1041b5;
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.3), 0 4px 16px rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.4), 0 8px 20px rgba(255, 255, 255, 0.3);
    color: #1041b5;
  }
}

/* Ensure all Ant Design buttons used via <a-button> share the same height and border-radius */
.ant-btn {
  border-radius: 25rem !important;
  padding: 10px 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* If specific button classes set different paddings/heights, keep visual consistency */
.primary-btn,
.cta-btn,
.hero-search-btn,
.chat-send-btn {
  border-radius: 25rem !important;
  padding: 10px 40px !important;
}



// Floating Chat Widget
.chat-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 320px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 1px solid rgba(94, 123, 243, 0.2);
  border-radius: 16px;
  box-shadow: 0 16px 32px rgba(12, 12, 13, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 20;
  backdrop-filter: blur(6px);
}

.chat-widget.is-minimized {
  width: 260px;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.85), rgba(16, 65, 181, 0.95));
  color: #fff;
}

.chat-header-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chat-header-name {
  font-size: 14px;
  font-weight: 600;
}

.chat-header-desc {
  font-size: 12px;
  opacity: 0.85;
}

.chat-toggle {
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 12px;
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.chat-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: #fff;
}

.chat-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
  background: #fff;
  max-height: 280px;
  min-height: 160px;
  overflow-y: auto;
}

.chat-msg {
  font-size: 13px;
  line-height: 1.6;
  padding: 10px 12px;
  border-radius: 12px;
  max-width: 100%;
  word-break: break-word;
}

.chat-msg-ai {
  align-self: flex-start;
  background: rgba(94, 123, 243, 0.08);
  color: #1041b5;
  border: 1px solid rgba(94, 123, 243, 0.18);
}

.chat-msg-user {
  align-self: flex-end;
  background: linear-gradient(135deg, rgba(140, 194, 248, 0.3), rgba(16, 65, 181, 0.45));
  color: #1041b5;
}

.chat-input {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(94, 123, 243, 0.04);
  border-top: 1px solid rgba(94, 123, 243, 0.1);
}

.chat-input-field {
  flex: 1;
}

.chat-send-btn {
  flex-shrink: 0;
  height: 36px;
}

.chat-fade-enter-active,
.chat-fade-leave-active {
  transition: opacity 0.2s ease;
}

.chat-fade-enter-from,
.chat-fade-leave-to {
  opacity: 0;
}

// Responsive
@media (max-width: 768px) {
  .modules-section,
  .season-section,
  .updates-section,
  .partners-section,
  .cta-section {
    padding: 40px 0;
  }
  
  .section-header {
    margin-bottom: 32px;
  }
  
  .footer {
    padding: 40px 16px 20px;
  }
  
  .hero-section {
    .hero-bg {
      padding: 80px 0 40px;
    }
  }
  
  .hero-search {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-search-input {
    width: 100%;
  }

  .hero-stats {
    justify-content: center;
  }

  .hero-stat {
    align-items: center;
  }

  .module-card,
  .season-card,
  .partner-card {
    padding: 24px;
  }

  .updates-ticker {
    padding: 12px;
  }

  .chat-widget {
    right: 16px;
    bottom: 16px;
    width: calc(100vw - 32px);
  }
}
</style>
